import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import colors from "../../Constants/colors";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useCreateGradingCriterion,
  useUpdateGradingCriterion,
  useGradingCriterion,
} from "../../Query/useGradingCriteria";
import { useQueryClient } from "@tanstack/react-query";

import { ComoInput } from "../Common/ComoInput";
import { ComoButton } from "../Common/ComoButton";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";

const GradingCriterionCreatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { gradingCriterionId } = useParams<{ gradingCriterionId?: string }>();
  const { t } = useTranslation("gradingCriteria");
  const queryClient = useQueryClient();
  const { colors: workspace } = useWorkspaceColors();

  const [name, setName] = useState("");
  const [criterion, setCriterion] = useState("");
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate: createMutate } = useCreateGradingCriterion();
  const { mutate: updateMutate } = useUpdateGradingCriterion();

  const {
    data: singleCriterion,
    isLoading: isFetching,
    isError: isFetchingError,
  } = useGradingCriterion
    ? useGradingCriterion(Number(gradingCriterionId))
    : { data: null, isLoading: false, isError: false };

  useEffect(() => {
    if (gradingCriterionId) setMode("edit");
    else setMode("create");

    setName("");
    setCriterion("");
  }, [gradingCriterionId]);

  useEffect(() => {
    if (mode === "edit" && singleCriterion) {
      setName(singleCriterion.name);
      setCriterion(singleCriterion.criterion);
    }
  }, [mode, singleCriterion]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (mode === "create") {
      createMutate(
        { name, criterion },
        {
          onSuccess: () => {
            setIsSubmitting(false);
            navigate("/grading_criterion");
          },
          onError: () => setIsSubmitting(false),
        },
      );
    } else {
      updateMutate(
        {
          id: Number(gradingCriterionId),
          payload: { name, criterion },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["gradingCriterion", Number(gradingCriterionId)],
            });
            setIsSubmitting(false);
            navigate("/grading_criterion");
          },
          onError: () => setIsSubmitting(false),
        },
      );
    }
  };

  if (isFetching) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 64px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colors.gray50,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isFetchingError) {
    navigate("/grading_criterion");
    return null;
  }

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: colors.gray50 }}
    >
      <Box sx={{ maxWidth: "lg", mx: "auto", px: 3, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton
              onClick={() => navigate("/grading_criterion")}
              size="small"
              sx={{ color: workspace.primary_dark }}
            >
              <ArrowBackIcon />
            </IconButton>

            <AssessmentOutlinedIcon
              sx={{ fontSize: 32, color: workspace.primary_dark }}
            />

            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: workspace.primary_dark }}
              >
                {mode === "create" ? t("createTitle") : t("editTitle")}
              </Typography>
            </Box>
          </Stack>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Box component="form" onSubmit={handleSubmit} key={location.pathname}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ComoInput
                  label={t("name")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={isSubmitting}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <ComoInput
                  label={t("criterion")}
                  value={criterion}
                  onChange={(e) => setCriterion(e.target.value)}
                  required
                  disabled={isSubmitting}
                  multiline
                  minRows={5}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <ComoButton
                  type="submit"
                  fullWidth
                  disabled={isSubmitting || !name.trim() || !criterion.trim()}
                  sx={{ py: 1.25, fontWeight: 600, mt: 1 }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : mode === "create" ? (
                    t("create")
                  ) : (
                    t("update")
                  )}
                </ComoButton>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default GradingCriterionCreatePage;
